/**
 * The linkage canvas and its toolbar. It turns the draft into nodes and
 * edges, and every gesture back into an edit of the draft: a drag moves a
 * node, a drag between handles connects two steps (refused, with the reason,
 * where it would close a loop or link into `first`), a click selects. How
 * it works, beside Arrange and Fit, reruns the editor's tour (Feature 72 D7);
 * the canvas and Add a step carry the tour's anchors.
 *
 * Resting the pointer on a node or a link, or the keyboard on it, opens its
 * detail card after a short pause; leaving it, dragging, or panning closes it.
 *
 * When the canvas's width changes (the inspector fitted its fields, a drag of
 * the handle ended, the window was resized), the graph is fitted into it once
 * the width has settled: the view moves, the nodes do not. A gate grown from a
 * gate link moves what follows right, so the graph is fitted then too.
 *
 * Add a gate, beside Add a step, puts an if-else or a switch gate on the
 * canvas, following nothing until a link is dragged into it (Feature 79 D28,
 * D29). A new step or gate lands in the middle of what the canvas shows,
 * clear of the other nodes, and is selected; the view does not move (D33).
 *
 * A new template's canvas starts empty (Feature 80 D2): the dropdown reads
 * Add the first node and lists capabilities until the first node exists,
 * which opens its form, and Add a gate waits for it (D9).
 */
import { useEffect, useMemo, useRef, useState, type FocusEvent } from 'react';

import type { LinkageDecisionMode, LinkageProblem } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Dropdown, DropdownItem } from '../../ui/Dropdown.js';
import { Tooltip } from '../../ui/Tooltip.js';
import { FlowCanvas } from '../../ui/FlowCanvas.js';
import { Inline, Stack, Typography } from '../../ui/finixt.js';
import type { Connection, Edge, EdgeTypes, NodeChange, NodeTypes } from '../../ui/flow.js';
import { useReactFlow } from '../../ui/flow.js';
import { Select, type SelectOption } from '../../ui/Select.js';
import { useToast } from '../../ui/useToast.js';
import { findCondition } from './conditions.js';
import { ConditionNode, type ConditionNodeType } from './ConditionNode.js';
import {
  addStep,
  FIRST,
  arrange,
  type ConnectRefusal,
  type Draft,
  moveNode,
  NODE_WIDTH,
  positions,
} from './draft.js';
import { GateNode, type GateNodeType } from './GateNode.js';
import { GATE_ROOM, landing, type Room } from './layout.js';
import { toFlow } from './to-flow.js';
import { HoveredContext } from './hovered.js';
import { nodeCard } from './node-card.js';
import type { Schemas } from './references.js';
import { RulesEdge, type RulesEdgeType } from './RulesEdge.js';
import { RETURN_IN, RETURN_OUT, StepNode, type StepNodeType } from './StepNode.js';
import { addGate, linkFrom } from './decisions.js';
import { addFirst, hasFirst } from './first.js';
import { WriteBackEdge, type WriteBackEdgeType } from './WriteBackEdge.js';
import type { Selection } from './selection.js';

const NODE_TYPES: NodeTypes = { step: StepNode, gate: GateNode, condition: ConditionNode };
const EDGE_TYPES: EdgeTypes = { rules: RulesEdge, writeBack: WriteBackEdge };
const CANVAS_HEIGHT = 560;
/** How long the pointer rests on a node or a link before its card opens. */
// How long the canvas's width must hold still before the graph is fitted again.
const REFIT_AFTER_MS = 200;
const HOVER_DELAY_MS = 300;
// A gate's ties to its condition nodes: pale, as drawn structure, not links.
const TIE_STYLE = { stroke: 'var(--blue)', strokeOpacity: 0.35, strokeWidth: 2 };
// About half a step's height, so a new node's middle lands on the view's.
const NODE_HALF_HEIGHT = 30;
// How far inside the canvas's shown edges a new node must stay.
const EDGE = 24;

const REFUSAL: Record<ConnectRefusal, string> = {
  into_first: 'Nothing links into first: it is the completed task.',
  into_decision: 'A gate node follows first or a step, never another gate or a condition node.',
  into_condition: 'Nothing links into a condition node: drag from it to a step.',
  self: 'A step cannot link to itself.',
  duplicate: 'These two are already linked.',
  loop: 'That link would close a loop.',
  unknown: 'One end is not a step.',
};

export function LinkageCanvas({
  draft,
  problems,
  selection,
  capabilities,
  schemas,
  onChange,
  onSelect,
  onTour,
}: {
  draft: Draft;
  problems: LinkageProblem[];
  selection: Selection;
  capabilities: readonly SelectOption[];
  /** The capabilities' schemas, for a node's card. */
  schemas: Schemas;
  onChange: (draft: Draft) => void;
  onSelect: (selection: Selection) => void;
  /** Runs the editor's tour from its first step. */
  onTour: () => void;
}) {
  const toast = useToast();
  const flow = useReactFlow();
  const [hovered, setHovered] = useState<string | null>(null);
  const pending = useRef<number | undefined>(undefined);
  const frame = useRef<HTMLDivElement>(null);

  useEffect(() => () => window.clearTimeout(pending.current), []);

  // Fit the graph again once a change of width has settled; the first reading is
  // the canvas's own first layout, which the canvas already fits.
  useEffect(() => {
    const element = frame.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let seen: number | null = null;
    let timer: number | undefined;
    const observer = new ResizeObserver((entries) => {
      const width = Math.round(entries[0]?.contentRect.width ?? 0);
      if (seen === null || width === seen) {
        seen = width;
        return;
      }
      seen = width;
      window.clearTimeout(timer);
      timer = window.setTimeout(
        () => void flow.fitView({ padding: 0.2, maxZoom: 1 }),
        REFIT_AFTER_MS,
      );
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [flow]);
  // A gate grown from a gate link moves what follows right (D30): fit the
  // graph so it stays in sight. A gate from the toolbar, which follows nothing
  // yet, and a renamed gate leave the view as it is.
  const gates = draft.decisions;
  const seen = useRef(gates.map((d) => d.name));
  useEffect(() => {
    const before = new Set(seen.current);
    const grown = gates.length > before.size && gates.some((d) => !before.has(d.name) && d.from);
    seen.current = gates.map((d) => d.name);
    if (grown) window.requestAnimationFrame(() => void flow.fitView({ padding: 0.2, maxZoom: 1 }));
  }, [gates, flow]);

  const first = hasFirst(draft);
  const card = hovered === null ? null : nodeCard(draft, hovered, schemas);
  const shown = useMemo(() => ({ id: hovered, card }), [hovered, card]);

  /** Opens a card after the pause; a newer rest or a leave replaces it. */
  function rest(id: string) {
    window.clearTimeout(pending.current);
    pending.current = window.setTimeout(() => setHovered(id), HOVER_DELAY_MS);
  }

  function leave() {
    window.clearTimeout(pending.current);
    setHovered(null);
  }

  /** The keyboard on a node or a link opens its card at once. */
  function onFocus(event: FocusEvent<HTMLDivElement>) {
    const item = (event.target as Element).closest('.react-flow__node, .react-flow__edge');
    const id = item?.getAttribute('data-id');
    if (id) {
      window.clearTimeout(pending.current);
      setHovered(id);
    }
  }

  const { nodes, edges } = useMemo(() => {
    const drawn = toFlow(draft, problems);
    const stepNodes: StepNodeType[] = drawn.nodes.map((n) => ({
      ...n,
      type: 'step',
      selected: selection?.kind === 'node' && selection.id === n.id,
    }));
    const ruleEdges: RulesEdgeType[] = drawn.edges.map((e) => ({
      ...e,
      type: 'rules',
      selected:
        selection?.kind === 'edge' && selection.from === e.source && selection.to === e.target,
    }));
    // A write-back runs between the hidden return handles, below the nodes.
    const returnEdges: WriteBackEdgeType[] = drawn.returns.map((r) => ({
      ...r,
      type: 'writeBack',
      sourceHandle: RETURN_OUT,
      targetHandle: RETURN_IN,
    }));
    const gateNodes: GateNodeType[] = drawn.gates.map((g) => ({
      ...g,
      type: 'gate',
      selected: selection?.kind === 'node' && selection.id === g.id,
    }));
    const conditionNodes: ConditionNodeType[] = drawn.conditions.map((n) => ({
      ...n,
      type: 'condition',
      selected: selection?.kind === 'node' && selection.id === n.id,
    }));
    // A gate's way in is a plain line from the node it follows (Feature 79).
    const wayEdges: Edge[] = drawn.ways.map((w) => ({
      ...w,
      type: 'smoothstep',
      data: { problems: [] },
      style: { stroke: 'var(--border-hover)', strokeWidth: 2 },
    }));
    const tieEdges: Edge[] = drawn.ties.map((t) => ({
      ...t,
      type: 'smoothstep',
      data: { problems: [] },
      style: TIE_STYLE,
    }));
    return {
      nodes: [...stepNodes, ...gateNodes, ...conditionNodes],
      edges: [...ruleEdges, ...wayEdges, ...tieEdges, ...returnEdges],
    };
  }, [draft, problems, selection]);

  function onNodesChange(changes: NodeChange<StepNodeType | GateNodeType | ConditionNodeType>[]) {
    let next = draft;
    for (const change of changes) {
      if (change.type === 'position' && change.position) {
        next = moveNode(next, change.id, change.position);
      }
    }
    if (next !== draft) onChange(next);
  }

  function onConnect({ source, target }: Connection) {
    const linked = linkFrom(draft, source, target);
    if (linked.refusal) {
      toast.error('Link refused', REFUSAL[linked.refusal]);
      return;
    }
    onChange(linked.draft);
    // A gate's way in is the gate's, a link out of a condition node that
    // node's, and a gate link its own.
    if (linked.draft.decisions.some((d) => d.name === target)) {
      onSelect({ kind: 'node', id: target });
    } else if (findCondition(linked.draft.decisions, source)) {
      onSelect({ kind: 'node', id: source });
    } else onSelect({ kind: 'edge', from: source, to: target });
  }

  /** What a click on an edge opens: its link's gate, or the node it belongs to. */
  function onEdge(edge: Edge) {
    // A write-back's arrow is part of its writer, not a link of its own.
    if (edge.type === 'writeBack') onSelect({ kind: 'node', id: edge.source });
    // A gate's way in is the gate's, a tie its condition node's, and a link out
    // of a condition node that node's.
    else if (edge.type === 'smoothstep') onSelect({ kind: 'node', id: edge.target });
    else if (findCondition(draft.decisions, edge.source)) {
      onSelect({ kind: 'node', id: edge.source });
    } else onSelect({ kind: 'edge', from: edge.source, to: edge.target });
  }

  /**
   * Where a new node lands: the middle of the part of the canvas the window
   * shows, moved to the nearest place clear of the other nodes and still in
   * that part.
   */
  function landingPoint(room?: Room) {
    const box = frame.current?.getBoundingClientRect();
    if (!box) return landing(Object.values(positions(draft)), { x: 0, y: 0 }, room);
    const top = Math.max(box.top, 0);
    const bottom = Math.min(box.bottom, window.innerHeight);
    const shown = top < bottom ? { top, bottom } : { top: box.top, bottom: box.bottom };
    const corner = flow.screenToFlowPosition({ x: box.left + EDGE, y: shown.top + EDGE });
    const far = flow.screenToFlowPosition({ x: box.right - EDGE, y: shown.bottom - EDGE });
    const middle = { x: (corner.x + far.x) / 2, y: (corner.y + far.y) / 2 };
    // The node's own middle, not its corner, sits there.
    return landing(
      Object.values(positions(draft)),
      { x: Math.round(middle.x - NODE_WIDTH / 2), y: Math.round(middle.y - NODE_HALF_HEIGHT) },
      room,
      { left: corner.x, top: corner.y, right: far.x, bottom: far.y },
    );
  }

  function onAdd(capabilityId: string) {
    if (!capabilityId) return;
    // The first pick on an empty canvas is the first node; it opens its form.
    if (!hasFirst(draft)) {
      onChange(addFirst(draft, capabilityId, landingPoint()));
      onSelect({ kind: 'node', id: FIRST });
      return;
    }
    const added = addStep(draft, capabilityId, landingPoint());
    onChange(added.draft);
    onSelect({ kind: 'node', id: added.name });
  }

  function onAddGate(mode: LinkageDecisionMode) {
    const added = addGate(draft, mode, landingPoint(GATE_ROOM));
    onChange(added.draft);
    onSelect({ kind: 'node', id: added.name });
  }

  return (
    <Stack gap={9}>
      <Inline gap={7} align="center">
        <span data-tour="add-step" style={{ display: 'inline-flex' }}>
          <Select
            searchable
            aria-label={first ? 'Add a step' : 'Add the first node'}
            placeholder={first ? 'Add a step…' : 'Add the first node…'}
            value=""
            onChange={onAdd}
            options={capabilities}
            width={260}
          />
        </span>
        {first ? (
          <Dropdown
            width={300}
            trigger={
              <Button variant="secondary" icon="add">
                Add a gate
              </Button>
            }
          >
            <DropdownItem
              label="If-else gate"
              sub="Conditions tried in order, then an else"
              onSelect={() => onAddGate('if')}
            />
            <DropdownItem
              label="Switch gate"
              sub="One field, a value or more per case, then a default"
              onSelect={() => onAddGate('switch')}
            />
          </Dropdown>
        ) : (
          // A gate follows first or a step, so it waits for the first node (Feature 80 D9).
          <Tooltip label="Add the first node first: a gate follows it or a step">
            <Button variant="secondary" icon="add" disabled>
              Add a gate
            </Button>
          </Tooltip>
        )}
        <Button
          variant="ghost"
          icon="account_tree"
          onClick={() => {
            onChange(arrange(draft));
            window.requestAnimationFrame(() => void flow.fitView({ padding: 0.2, maxZoom: 1 }));
          }}
        >
          Arrange
        </Button>
        <Button
          variant="ghost"
          icon="fit_screen"
          onClick={() => void flow.fitView({ padding: 0.2, maxZoom: 1 })}
        >
          Fit
        </Button>
        <Button variant="ghost" icon="help" onClick={onTour}>
          How it works
        </Button>
      </Inline>
      {/* The tour's anchor: FlowCanvas takes no attributes of its own. */}
      <div
        ref={frame}
        data-tour="canvas"
        onFocus={onFocus}
        onBlur={leave}
        style={{ position: 'relative' }}
      >
        {!first && (
          <Typography
            variant="meta"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: 26,
              pointerEvents: 'none',
            }}
          >
            The canvas is empty. Start with the first node: pick its capability in Add the first
            node.
          </Typography>
        )}
        <HoveredContext.Provider value={shown}>
          <FlowCanvas<
            StepNodeType | GateNodeType | ConditionNodeType,
            RulesEdgeType | WriteBackEdgeType | Edge
          >
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            height={CANVAS_HEIGHT}
            onNodesChange={onNodesChange}
            onConnect={onConnect}
            isValidConnection={(c) => linkFrom(draft, c.source, c.target).refusal === null}
            onNodeClick={(_event, node) => onSelect({ kind: 'node', id: node.id })}
            onEdgeClick={(_event, edge) => onEdge(edge)}
            onPaneClick={() => onSelect(null)}
            onNodeMouseEnter={(_event, node) => rest(node.id)}
            onNodeMouseLeave={leave}
            // A write-back's arrow, a way in, a tie, and a branch have no card of their own.
            onEdgeMouseEnter={(_event, edge) =>
              edge.type === 'rules' && !findCondition(draft.decisions, edge.source)
                ? rest(edge.id)
                : undefined
            }
            onEdgeMouseLeave={leave}
            onNodeDragStart={leave}
            onMoveStart={leave}
          />
        </HoveredContext.Provider>
      </div>
    </Stack>
  );
}
